import {computed, type Ref} from 'vue';
import {storeToRefs} from 'pinia';
import {useLegalEntityStore} from '~/store/organization-settings/legal-entity-store';
import type {NavigationLink, NavigationLinksRecord} from '~/types/navigation';

const ROUTES = {
    DETAILS: 'DETAILS',
    BILLING: 'BILLING',
    TEAMS: 'TEAMS',
    MEMBERS: 'MEMBERS',
    INTEGRATION: 'INTEGRATION',
    SETTINGS: 'SETTINGS',
};

type RouteSetup = {
    integrationGatewayEnabled: boolean;
    isPlatformAdmin: boolean;
    organizationId: Ref<string | undefined>;
    isTeamsPageEnabled: boolean;
    hasTeamSettingsAccessRights: boolean;
    hasLegalEntityEditionRights: boolean;
};

export function useNavigation(setup: RouteSetup) {
    const {incompleteLegalEntityCount} = storeToRefs(useLegalEntityStore());

    const navigationLinks = computed<NavigationLinksRecord>(() => {
        const organizationId = setup.organizationId.value;

        const integrationMarketplaceRoute: NavigationLink = {
            id: ROUTES.INTEGRATION,
            to: `/integration/${organizationId}`,
            external: true,
            activePattern: /\/organization\/.*\/integration/,
        };

        const integrationChildrenRoutes: NavigationLink[] = [];
        if (setup.integrationGatewayEnabled && setup.isPlatformAdmin) {
            integrationChildrenRoutes.push(integrationMarketplaceRoute);
        }

        const integrationRoute = {
            id: ROUTES.INTEGRATION,
            to: {},
            isComingSoon: integrationChildrenRoutes.length === 0,
            children: integrationChildrenRoutes,
            activePattern: /\/organization\/.*\/(integration|tokens)/,
        };

        const links: NavigationLinksRecord = [
            {
                id: ROUTES.DETAILS,
                to: {
                    name: 'organization-id-details',
                    params: {id: organizationId},
                },
                activePattern: /\/organization\/.*\/details/,
            },
            {
                id: ROUTES.BILLING,
                to: {
                    name: 'organization-id-legal-entity-list',
                    params: {id: organizationId},
                },
                notificationCount: incompleteLegalEntityCount.value,
                activePattern: /\/legal-entity\/.*/,
                constraint: setup.hasLegalEntityEditionRights,
            },
            {
                id: ROUTES.TEAMS,
                to: setup.isTeamsPageEnabled
                    ? {
                          name: 'organization-id-teams',
                          params: {id: organizationId},
                      }
                    : {},
                isComingSoon: !setup.isTeamsPageEnabled,
                constraint: setup.hasTeamSettingsAccessRights,
                activePattern: /\/organization\/.*\/teams/,
            },
            {
                id: ROUTES.MEMBERS,
                to: {
                    // name: 'organization-id-members',
                },
                isComingSoon: true,
                activePattern: /\/organization\/[^/]+\/members/,
            },
            integrationRoute,
        ];

        if (setup.isPlatformAdmin) {
            links.push({
                id: ROUTES.SETTINGS,
                to: {
                    name: 'organization-id-settings',
                    params: {id: organizationId},
                },
                isComingSoon: false,
                activePattern: /\/organization\/.*\/settings/,
            });
        }

        return links;
    });

    return {
        navigationLinks,
        ROUTES,
    };
}
